import { useEffect, useRef } from 'react';

import { loadTurnstile, type TurnstileApi, type TurnstileLoader } from './turnstile';

interface TurnstileChallengeProps {
  loader?: TurnstileLoader | undefined;
  onError: () => void;
  onToken: (token: string | null) => void;
  resetKey: number;
  siteKey: string;
}

export function TurnstileChallenge({
  loader = loadTurnstile,
  onError,
  onToken,
  resetKey,
  siteKey,
}: TurnstileChallengeProps) {
  const apiRef = useRef<TurnstileApi | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resetKeyRef = useRef(resetKey);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    const container = containerRef.current;
    if (container === null) return;

    void loader()
      .then((api) => {
        if (!active) return;
        apiRef.current = api;
        widgetIdRef.current = api.render(container, {
          action: 'contact_submit',
          callback: (token) => onToken(token),
          'error-callback': () => {
            onToken(null);
            onError();
          },
          'expired-callback': () => onToken(null),
          sitekey: siteKey,
        });
      })
      .catch(() => {
        if (active) onError();
      });

    return () => {
      active = false;
      const api = apiRef.current;
      const widgetId = widgetIdRef.current;
      if (api !== null && widgetId !== null) api.remove(widgetId);
      apiRef.current = null;
      widgetIdRef.current = null;
    };
  }, [loader, onError, onToken, siteKey]);

  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    onToken(null);
    const api = apiRef.current;
    const widgetId = widgetIdRef.current;
    if (api !== null && widgetId !== null) api.reset(widgetId);
  }, [onToken, resetKey]);

  return (
    <div className="contact-challenge">
      <div
        aria-label="Cloudflare Turnstile human verification"
        ref={containerRef}
      />
      <p>
        Human verification is provided by Cloudflare Turnstile. The token is
        discarded after each attempt.
      </p>
    </div>
  );
}
